import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function CleaningAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const canAccess = user?.role === "admin" || user?.role === "cleaning_admin";

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [u, b] = await Promise.all([
        api.get("/cleaning-admin/users"),
        api.get("/blocks"),
      ]);
      setUsers(u.data || []);
      setBlocks(b.data || []);
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => { load(); }, [load]);

  const blockLabels = useMemo(() => {
    const labels = new Map();
    blocks.forEach((block) => labels.set(block.id, `${block.code} · ${block.name}`));
    return labels;
  }, [blocks]);

  if (!canAccess) {
    return (
      <div className="bg-white border border-gray-200 p-8 text-center" data-testid="cleaning-admin-access-denied">
        <h1 className="font-chivo font-black text-2xl mb-2">No access</h1>
        <p className="text-sm text-gray-600">You do not have access to the cleaners page.</p>
      </div>
    );
  }

  return (
    <div data-testid="cleaning-admin-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-4xl tracking-tight">Cleaners</h1>
          <p className="text-sm text-gray-600 mt-1">Review cleaner accounts and manage their block assignments.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[2fr_1fr_2fr] gap-3 px-4 py-2 border-b border-gray-200 text-[10px] font-mono uppercase tracking-wider text-gray-500">
              <div>User</div>
              <div>Role</div>
              <div>Allowed blocks</div>
            </div>
            {users.map((cleaner) => (
              <CleanerRow key={cleaner.id} cleaner={cleaner} blocks={blocks} blockLabels={blockLabels} onSaved={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CleanerRow({ cleaner, blocks, blockLabels, onSaved }) {
  const [allBlocks, setAllBlocks] = useState(cleaner.allowed_blocks?.includes("*"));
  const [blockIds, setBlockIds] = useState(cleaner.allowed_blocks?.includes("*") ? [] : cleaner.allowed_blocks || []);
  const [saving, setSaving] = useState(false);
  const isCleaner = cleaner.role === "cleaner";

  const toggleBlock = (id) => {
    setBlockIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/cleaning-admin/users/${cleaner.id}/blocks`, {
        allowed_blocks: allBlocks ? ["*"] : blockIds,
      });
      toast.success("Cleaner blocks updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update cleaner");
    } finally {
      setSaving(false);
    }
  };

  const currentBlocks = cleaner.allowed_blocks?.includes("*")
    ? "All blocks"
    : (cleaner.allowed_blocks || []).map((id) => blockLabels.get(id) || id).join(", ") || "No blocks";

  return (
    <div className="grid grid-cols-[2fr_1fr_2fr] gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 items-start" data-testid={`cleaning-admin-user-${cleaner.email}`}>
      <div>
        <div className="font-chivo font-bold text-sm">{cleaner.name}</div>
        <div className="text-[11px] font-mono text-gray-500">{cleaner.email}</div>
      </div>
      <div>
        <span className="text-[10px] font-mono font-bold uppercase tracking-wider border border-gray-300 px-1.5 py-0.5">{cleaner.role}</span>
      </div>
      <div className="space-y-2">
        <div className="text-[11px] font-mono text-gray-600">Current: {currentBlocks}</div>
        {isCleaner ? (
          <>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" className="w-4 h-4 accent-black" checked={allBlocks} onChange={(e) => setAllBlocks(e.target.checked)} data-testid={`cleaner-${cleaner.id}-all-blocks`} />
              All blocks
            </label>
            {!allBlocks && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-44 overflow-y-auto border border-gray-100 p-2">
                {blocks.map((block) => (
                  <label key={block.id} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" className="w-4 h-4 accent-black" checked={blockIds.includes(block.id)} onChange={() => toggleBlock(block.id)} data-testid={`cleaner-${cleaner.id}-block-${block.id}`} />
                    {block.code} · {block.name}
                  </label>
                ))}
              </div>
            )}
            <button disabled={saving} onClick={save} data-testid={`cleaner-${cleaner.id}-save`} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-black bg-black text-white disabled:opacity-40">
              {saving ? "Saving…" : "Save blocks"}
            </button>
          </>
        ) : (
          <div className="text-xs text-gray-500">Cleaning admins can be viewed here; block editing is for cleaners only.</div>
        )}
      </div>
    </div>
  );
}
